import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from "react";
import { View, Text, StyleSheet, TouchableOpacity, Platform, Dimensions } from "react-native";
import Svg, { Path } from "react-native-svg";

type ToastVariant = "success" | "info" | "warning" | "error";
type ToastPosition =
  | "top"
  | "bottom"
  | "top-right"
  | "top-left"
  | "bottom-right"
  | "bottom-left";

interface ToastProps {
  header: string;
  subtext: string;
  variant?: ToastVariant;
  position?: ToastPosition;
  duration?: number; // Optional duration in ms
  hideOnBackgroundClick?: boolean; // Hide when clicking outside the toast (web)
  hideOnUserScroll?: boolean; // Hide after user-initiated scrolling (web)
  userScrollDismissDelayMs?: number; // Delay before hiding after user scroll
  onHide?: () => void; // Optional callback to hide toast
  actionLabel?: string;
  onActionPress?: () => void;
}

interface ToastContextType {
  showToast: (props: ToastProps) => void;
  show: (message: string, variant?: ToastVariant, header?: string) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const LhToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [toast, setToast] = useState<ToastProps | null>(null);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const toastWrapperRef = useRef<any>(null);

  const showToast = (props: ToastProps) => {
    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    setToast(props);

    // duration <= 0 means persistent until explicitly dismissed.
    const effectiveDuration = props.duration ?? 3000;
    if (effectiveDuration > 0) {
      const id = setTimeout(() => {
        setToast(null);
      }, effectiveDuration);
      setTimeoutId(id);
    }
  };

  const hideToast = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setToast(null);
  };

  // Compatibility helper used by Ticketing Portal screens.
  const show = (message: string, variant: ToastVariant = "info", header = "") => {
    showToast({
      header,
      subtext: message,
      variant,
    });
  };

  useEffect(() => {
    if (!toast?.hideOnBackgroundClick || Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }

    const handleOutsideClickDismiss = (event: MouseEvent | TouchEvent) => {
      const wrapperEl = toastWrapperRef.current;
      const target = event.target as Node | null;

      if (wrapperEl?.contains && target && wrapperEl.contains(target)) {
        return;
      }

      hideToast();
    };

    window.addEventListener('mousedown', handleOutsideClickDismiss);
    window.addEventListener('touchstart', handleOutsideClickDismiss, { passive: true });

    return () => {
      window.removeEventListener('mousedown', handleOutsideClickDismiss);
      window.removeEventListener('touchstart', handleOutsideClickDismiss);
    };
  }, [toast]);

  useEffect(() => {
    if (!toast?.hideOnUserScroll || Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }

    const toastShownAt = Date.now();
    const initialAutoScrollIgnoreMs = 5000;

    const onScroll = () => {
      const elapsedMs = Date.now() - toastShownAt;

      // Ignore all scroll dismissals for the initial grace window.
      if (elapsedMs < initialAutoScrollIgnoreMs) {
        return;
      }

      hideToast();
    };

    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [toast]);

  // Get the appropriate wrapper style based on the position
  const getPositionStyle = (position?: ToastPosition, variant?: ToastVariant) => {
    // If position is explicitly provided, use it
    if (position === "top") {
      return [styles.toastWrapper, styles.topPosition];
    }
    if (position === "top-right") {
      return [styles.toastWrapper, styles.topRightPosition];
    }
    
    // For success variant, position it lower on mobile (only if no explicit position)
    if (variant === "success" && !position) {
      return [styles.toastWrapper, styles.successPosition];
    }

    // For error variant, always show near the top and centered for visibility
    if (variant === "error") {
      return [styles.toastWrapper, styles.errorPosition];
    }

    switch (position) {
      case "top-left":
        return [styles.toastWrapper, styles.topLeftPosition];
      case "bottom-right":
        return [styles.toastWrapper, styles.bottomRightPosition];
      case "bottom-left":
        return [styles.toastWrapper, styles.bottomLeftPosition];
      case "bottom":
      default:
        return [styles.toastWrapper, styles.bottomPosition];
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, show, hideToast }}>
      {children}
      {toast && (
        <View ref={toastWrapperRef} style={getPositionStyle(toast.position, toast.variant)}>
          <LhToast {...toast} onHide={hideToast} />
        </View>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a LhToastProvider");
  }
  return context;
};

// Tick Icon from Figma
const TickIcon = () => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path 
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z" 
      fill="white"
    />
  </Svg>
);

// Info Icon: circular background with white 'i' to visually match success icon
const InfoIcon = () => (
  <View style={styles.infoIconCircle}>
    <Text style={styles.infoIconText}>i</Text>
  </View>
);

// Close Icon from Figma
const CloseIcon = ({ color = "white" }: { color?: string }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path 
      d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" 
      fill={color}
    />
  </Svg>
);

const LhToast: React.FC<ToastProps> = ({
  header,
  subtext,
  variant = "info",
  onHide,
  actionLabel,
  onActionPress,
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "success":
        return {
          container: styles.successContainer,
          border: styles.successBorder,
          icon: "✓",
          iconColor: "#FFFFFF",
          showBorder: false,
          showCloseButton: true,
        };
      case "info":
        // Keep the original light info background but make icon, spacing and typography match success
        return {
          container: styles.infoContainer, // preserve light background
          border: styles.infoBorder,
          icon: "i",
          iconColor: "#0366d6", // blue icon color (circle will be blue with white i)
          showBorder: false,
          showCloseButton: true,
        };
      case "warning":
        return {
          container: styles.warningContainer,
          border: styles.warningBorder,
          icon: "⚠",
          iconColor: "#000000",
          showBorder: true,
          showCloseButton: true,
        };
      case "error":
        return {
          container: styles.errorContainer,
          border: styles.errorBorder,
          icon: "×",
          iconColor: "#f87171",
          showBorder: true,
          showCloseButton: false,
        };
      default:
        return {
          container: styles.infoContainer,
          border: styles.infoBorder,
          icon: "i",
          iconColor: "#60a5fa",
          showBorder: true,
          showCloseButton: false,
        };
    }
  };

  const variantStyles = getVariantStyles();
  // Determine close icon color so it is visible on the variant background
  const closeIconColor = variant === 'info' ? '#034895' : (variant === 'success' ? 'white' : (variant === 'warning' ? '#000000' : 'white'));

  return (
    <View style={[styles.container, variantStyles.container]}>
      {variantStyles.showBorder && <View style={[styles.border, variantStyles.border]} />}
      {variant === 'success' ? (
        <View style={styles.successIconContainer}>
          <TickIcon />
        </View>
      ) : variant === 'info' ? (
        <View style={styles.successIconContainer}>
          <InfoIcon />
        </View>
      ) : (
        <Text style={[styles.iconText, { color: variantStyles.iconColor }]}> 
          {variantStyles.icon}
        </Text>
      )}
      <View style={styles.textContainer}>
        {subtext ? (
          <>
             {header && (
               <Text style={[styles.headerText, variant === 'success' && { color: '#FFFFFF' }, variant === 'info' && { color: '#000000' }, variant === 'warning' && { color: '#000000' }, variant === 'error' && { color: '#FFFFFF' }]}>
                 {header}
               </Text>
             )}
             <Text style={[styles.subtext, variant === 'success' && { color: '#FFFFFF' }, variant === 'info' && { color: '#000000' }, variant === 'warning' && { color: '#000000' }, variant === 'error' && { color: '#FFFFFF' }]}>
               {subtext}
             </Text>
            {actionLabel && onActionPress ? (
              <TouchableOpacity onPress={onActionPress}>
                <Text
                  style={[
                    styles.actionText,
                    variant === "success" ? styles.actionTextSuccess : styles.actionTextInfo,
                  ]}
                >
                  {actionLabel}
                </Text>
              </TouchableOpacity>
            ) : null}
          </>
        ) : (
          <Text style={[styles.singleLineText, variant === 'success' && { color: '#FFFFFF' }, variant === 'info' && { color: '#000000' }, variant === 'warning' && { color: '#000000' }, variant === 'error' && { color: '#FFFFFF' }]}> 
            {header}
          </Text>
        )}
      </View>
      {variantStyles.showCloseButton && onHide && (
        <TouchableOpacity 
          onPress={onHide}
          style={styles.closeButton}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <CloseIcon color={closeIconColor} />
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  toastWrapper: {
    ...Platform.select({
      web: { position: "fixed" } as any,
      default: { position: "absolute" },
    }),
    bottom: 20,
    left: 20,
    right: 20,
    alignItems: "center",
    zIndex: 9999,
  },
  topPosition: {
    bottom: undefined,
    top: Platform.OS === "web" ? 70 : 80,
    left: 20,
    right: 20,
    alignItems: "center",
  },
  topRightPosition: {
    bottom: undefined,
    top: Platform.OS === "web" ? 70 : 80,
    left: undefined,
    right: 20,
    alignItems: "flex-end",
  },
  topLeftPosition: {
    bottom: undefined,
    top: 20,
    right: undefined,
    left: 20,
    alignItems: "flex-start",
  },
  bottomRightPosition: {
    bottom: 20,
    left: undefined,
    right: 20,
    alignItems: "flex-end",
  },
  bottomLeftPosition: {
    bottom: 20,
    right: undefined,
    left: 20,
    alignItems: "flex-start",
  },
  bottomPosition: {
    bottom: 20,
    left: 20,
    right: 20,
    alignItems: 'center',
  },
  successPosition: {
    top: Platform.OS === "web" ? 70 : Platform.OS === "ios" && Dimensions.get("window").width > 600 ? 120 : 180,
  },
  errorPosition: {
    // Keep error messages visible and centered near the top across platforms
    bottom: undefined,
    top: Platform.OS === "web" ? 70 : 80,
    alignItems: "center",
    left: 20,
    right: 20,
  },
  container: {
    flexDirection: "row",
    padding: 8,
    paddingHorizontal: 16,
    borderRadius: 4,
    minWidth: 280,
    maxWidth: 800,
    width: "auto",
    minHeight: 56,
    height: 'auto',
    shadowColor: "#005EC0",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.08,
    shadowRadius: 24,
    elevation: 3,
    gap: 0,
    alignItems: "center",
  },
  border: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  iconContainer: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 6,
  },
  successIconContainer: {
    width: 28,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  infoIconCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#0366d6', // blue circle to match icon style
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoIconText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 14,
  },
  iconText: {
    fontWeight: "bold",
    fontSize: 20,
    marginRight: 3,
  },
  successIconText: {
    fontSize: 22,
    fontWeight: "900",
  },
  textContainer: {
    flex: 1,
  },
  headerText: {
    fontWeight: "bold",
    fontSize: 16,
    marginBottom: 4,
  },
  subtext: {
    fontSize: 14,
  },
  actionText: {
    marginTop: 2,
    fontSize: 14,
    fontWeight: "700",
    textDecorationLine: "underline",
  },
  actionTextSuccess: {
    color: "#FFFFFF",
  },
  actionTextInfo: {
    color: "#034895",
  },
  singleLineText: {
    fontSize: 14,
    fontWeight: "400",
    fontFamily: "Inter",
  },
  closeButton: {
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
    marginLeft: 8,
  },
  successContainer: {
    backgroundColor: "#027C06",
    borderLeftWidth: 3,
    borderLeftColor: "#027C06",
  },
  successBorder: {
    backgroundColor: "#027C06",
  },
  infoContainer: {
    backgroundColor: "#FFFFFF",
    borderLeftWidth: 4,
    borderLeftColor: "#0366d6",
  },
  infoSolidContainer: {
    backgroundColor: "#FFFFFF",
    borderLeftWidth: 4,
    borderLeftColor: "#0366d6",
  },
  infoBorder: {
    backgroundColor: "#0366d6",
  },
  warningContainer: {
    backgroundColor: "#E5A408",
  },
  warningBorder: {
    backgroundColor: "#E5A408",
  },
  errorContainer: {
    backgroundColor: "#d32f2f",
  },
  errorBorder: {
    backgroundColor: "#b71c1c",
  },
});

export default LhToast;
